import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { FiPlay, FiPause } from "react-icons/fi";
import { CaptionEngine } from "../../video/subs/render";
import { clockText } from "../../video/upscale/plan";

// The video with its subtitles on it, exactly as they will be saved. `seek(t, play)` (through the ref) moves it; `onTime(t)` tells the page where it is.
const SubtitlePreview = forwardRef(function SubtitlePreview({ opened, look, onTime }, ref) {
  const { video, w, h, seconds } = opened;
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const lookRef = useRef(look);
  lookRef.current = look;
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const scale = Math.min(1, 960 / Math.max(w, h));
  const cw = Math.max(2, Math.round(w * scale));
  const ch = Math.max(2, Math.round(h * scale));

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || video.readyState < 2) return;
    if (!engineRef.current) { engineRef.current = new CaptionEngine(canvas); engineRef.current.setOutput(cw, ch); }
    engineRef.current.render(video, lookRef.current);
  }, [video, cw, ch]);

  useEffect(() => { engineRef.current = null; draw(); }, [cw, ch, draw]);
  useEffect(() => { draw(); }, [look, draw]);
  useEffect(() => {
    const again = () => { draw(); setTime(video.currentTime); if (onTime) onTime(video.currentTime); };
    video.addEventListener("seeked", again);
    video.addEventListener("loadeddata", again);
    video.muted = false;
    video.loop = false;
    return () => { video.removeEventListener("seeked", again); video.removeEventListener("loadeddata", again); video.pause(); };
  }, [video, draw, onTime]);
  useEffect(() => {
    if (!playing) return undefined;
    let stop = false;
    let id = 0;
    const tick = () => {
      if (stop) return;
      draw();
      setTime(video.currentTime);
      if (onTime) onTime(video.currentTime);
      if (video.ended) { setPlaying(false); return; }
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(tick); else id = requestAnimationFrame(tick);
    };
    tick();
    return () => { stop = true; cancelAnimationFrame(id); };
  }, [playing, video, draw, onTime]);

  useImperativeHandle(ref, () => ({
    seek(t, play = false) {
      video.currentTime = Math.max(0, t);
      if (play) video.play().then(() => setPlaying(true)).catch(() => {});
    },
    time: () => video.currentTime,
  }), [video]);

  const toggle = () => {
    if (playing) { video.pause(); setPlaying(false); draw(); setTime(video.currentTime); } else { video.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  return (
    <div>
      <div className="rounded-xl overflow-hidden bg-black flex justify-center">
        <canvas ref={canvasRef} width={cw} height={ch} className="block max-w-full max-h-[520px] w-auto" style={{ aspectRatio: `${cw} / ${ch}` }} aria-label="The video with its subtitles" />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button type="button" onClick={toggle} className="w-10 h-10 rounded-full bg-brand-gradient text-white flex items-center justify-center shrink-0" aria-label={playing ? "Pause" : "Play"}>{playing ? <FiPause /> : <FiPlay />}</button>
        <input type="range" min="0" max={Math.max(0.1, seconds)} step="0.05" value={Math.min(time, seconds || 0.1)} aria-label="Position in the video"
          onChange={(e) => { const t = Number(e.target.value); video.currentTime = t; setTime(t); }} className="flex-1 accent-[#22d3ee]" />
        <span className="text-xs text-gray-400 tabular-nums w-24 text-end">{clockText(time)} / {clockText(seconds)}</span>
      </div>
    </div>
  );
});

export default SubtitlePreview;
